import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Shield, Copy, AlertTriangle } from "lucide-react";

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ label: "", api_key: "", api_secret: "", uid: "", ip_whitelist: "" });

  const fetchKeys = useCallback(async () => {
    try {
      const r = await api.get("/keys");
      setKeys(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const addKey = async () => {
    if (!form.label || !form.api_key || !form.api_secret) { toast.error("Fill all required fields"); return; }
    try {
      await api.post("/keys", form);
      toast.success("API key added and encrypted");
      setDialogOpen(false);
      setForm({ label: "", api_key: "", api_secret: "", uid: "", ip_whitelist: "" });
      fetchKeys();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add key"); }
  };

  const deleteKey = async (id) => {
    try {
      await api.delete(`/keys/${id}`);
      toast.success("API key removed");
      fetchKeys();
    } catch (e) { toast.error("Failed to delete key"); }
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API Key Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Securely manage your MEXC exchange API keys</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-key-btn"><Plus className="w-4 h-4 mr-2" />Add API Key</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add MEXC API Key</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider">Label *</Label>
                <Input value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} placeholder="Main Trading Key" className="bg-secondary/50" data-testid="key-label-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider">API Key *</Label>
                <Input value={form.api_key} onChange={(e) => setForm((p) => ({ ...p, api_key: e.target.value }))} placeholder="mx0vgl..." className="bg-secondary/50 font-mono text-sm" data-testid="key-apikey-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider">API Secret *</Label>
                <Input type="password" value={form.api_secret} onChange={(e) => setForm((p) => ({ ...p, api_secret: e.target.value }))} placeholder="Enter API secret" className="bg-secondary/50 font-mono text-sm" data-testid="key-secret-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider">MEXC UID</Label>
                  <Input value={form.uid} onChange={(e) => setForm((p) => ({ ...p, uid: e.target.value }))} placeholder="30685453" className="bg-secondary/50 font-mono text-sm" data-testid="key-uid-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider">IP Whitelist</Label>
                  <Input value={form.ip_whitelist} onChange={(e) => setForm((p) => ({ ...p, ip_whitelist: e.target.value }))} placeholder="1.2.3.4, 5.6.7.8" className="bg-secondary/50 font-mono text-sm" data-testid="key-ip-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={addKey} data-testid="submit-add-key">Add Key</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* IP Whitelist Guidance */}
      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardContent className="py-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-400">IP Whitelist Recommended</p>
            <p className="text-xs text-muted-foreground mt-1">
              For security, we recommend setting up IP whitelisting on your MEXC API keys. Go to MEXC &rarr; API Management &rarr; Edit your key &rarr; Add server IP to the whitelist. This prevents unauthorized access even if keys are compromised.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Keys List */}
      {loading ? (
        <div className="space-y-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
      ) : keys.length === 0 ? (
        <Card className="bg-card border-border/50 py-16 text-center">
          <KeyRound className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No API keys added yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Add your MEXC API key to start trading</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map((k) => (
            <Card key={k.id} className="bg-card border-border/50" data-testid={`key-card-${k.id}`}>
              <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{k.label}</p>
                    <p className="font-mono text-xs text-muted-foreground mt-0.5">{k.api_key_masked}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {k.uid && <span className="text-[10px] text-muted-foreground">UID: {k.uid}</span>}
                      {k.ip_whitelist && <Badge variant="outline" className="text-[10px]">IP Protected</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/15 text-emerald-500 border-transparent text-[10px]">Encrypted</Badge>
                  <Button size="sm" variant="outline" onClick={() => deleteKey(k.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-key-${k.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
